import { inject, Injectable } from '@angular/core';

import type { ProductUpdate } from '@product-updates/shared-types';
import { getMonth, getQuarter, getYear, startOfQuarter, endOfQuarter } from 'date-fns';

import { I18nService } from '@core/i18n-legacy';

/**
 * Represents a month with its associated posts
 */
export interface MonthData {
  id: string; // '2025-01' (YYYY-MM format)
  label: string; // 'January'
  year: number;
  month: number; // 1-12
  posts: ProductUpdate[];
}

/**
 * Represents a quarter group containing 3 months
 */
export interface QuarterGroup {
  id: string; // 'Q1-2025'
  label: string; // 'Q1 2025'
  year: number;
  quarter: number; // 1-4
  months: MonthData[]; // Always 3 months per quarter
}

/**
 * @deprecated Use QuarterGroup instead
 * Represents a quarter with its associated posts (legacy)
 */
export interface QuarterData {
  id: string; // 'Q1-2025'
  label: string; // 'Q1 2025'
  year: number;
  quarter: number; // 1-4
  startDate: Date;
  endDate: Date;
  posts: ProductUpdate[];
}

// Month keys for translation lookup
const MONTH_KEYS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

@Injectable({ providedIn: 'root' })
export class TimelineDataService {
  private readonly i18nService = inject(I18nService);
  /**
   * Groups posts by quarter and returns sorted quarter data
   * Excludes Changelog posts, includes What's new and Coming soon
   */
  public groupPostsByQuarter(posts: ProductUpdate[]): QuarterData[] {
    // Filter: include What's new and Coming soon, exclude Changelog
    const filteredPosts = posts.filter(post => {
      const hasChangelog = post.updateCategories.includes('Changelog');
      // Exclude changelog-only posts
      if (hasChangelog && post.updateCategories.length === 1) {
        return false;
      }
      return !hasChangelog;
    });

    // Group posts by quarter
    const quarterMap = new Map<string, QuarterData>();

    for (const post of filteredPosts) {
      const publishedDate = new Date(post.publishedAt);
      const { year, quarter } = this.getQuarterFromDate(publishedDate);
      const quarterId = `Q${quarter}-${year}`;

      if (!quarterMap.has(quarterId)) {
        quarterMap.set(quarterId, {
          id: quarterId,
          label: this.getQuarterLabel(quarter, year),
          year,
          quarter,
          startDate: startOfQuarter(publishedDate),
          endDate: endOfQuarter(publishedDate),
          posts: [],
        });
      }

      quarterMap.get(quarterId)!.posts.push(post);
    }

    // Sort quarters chronologically (oldest first - left to right on timeline)
    const sortedQuarters = Array.from(quarterMap.values()).sort((a, b) => {
      if (a.year !== b.year) {
        return a.year - b.year;
      }
      return a.quarter - b.quarter;
    });

    // Sort posts within each quarter by date (oldest first)
    for (const quarter of sortedQuarters) {
      quarter.posts.sort(
        (a, b) => new Date(a.publishedAt).getTime() - new Date(b.publishedAt).getTime(),
      );
    }

    return sortedQuarters;
  }

  /**
   * Returns the current quarter ID (e.g., 'Q4-2025')
   */
  public getCurrentQuarterId(): string {
    const now = new Date();
    return `Q${getQuarter(now)}-${getYear(now)}`;
  }

  /**
   * Extracts year and quarter from a date
   */
  public getQuarterFromDate(date: Date): { year: number; quarter: number } {
    return {
      year: getYear(date),
      quarter: getQuarter(date),
    };
  }

  /**
   * Returns condensed label for mobile (e.g., "Q1'25" or "T1'25" for French)
   */
  public getCondensedLabel(quarterId: string): string {
    const match = quarterId.match(/Q(\d)-(\d{4})/);
    if (!match) return quarterId;
    const [, quarter, year] = match;
    const prefix = this.getQuarterPrefix();
    return `${prefix}${quarter}'${year.slice(2)}`;
  }

  // ============================================
  // NEW: Month-based grouping methods
  // ============================================

  /**
   * Groups posts by month and organizes them into quarter groups
   * Returns QuarterGroup[] with months as columns
   */
  public groupPostsByMonth(posts: ProductUpdate[]): QuarterGroup[] {
    const filteredPosts = this.filterNonChangelogPosts(posts);

    if (filteredPosts.length === 0) {
      return [];
    }

    // Find date range from posts
    const dates = filteredPosts.map(p => new Date(p.publishedAt));
    const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));

    const minYear = getYear(minDate);
    const maxYear = getYear(maxDate);
    const minQuarter = getQuarter(minDate);
    const maxQuarter = getQuarter(maxDate);

    // Group posts by month
    const monthMap = new Map<string, ProductUpdate[]>();
    for (const post of filteredPosts) {
      const publishedDate = new Date(post.publishedAt);
      const monthId = this.getMonthId(getYear(publishedDate), getMonth(publishedDate) + 1);
      if (!monthMap.has(monthId)) {
        monthMap.set(monthId, []);
      }
      monthMap.get(monthId)!.push(post);
    }

    // Build quarter groups with all months in range
    const quarterGroups: QuarterGroup[] = [];

    for (let year = minYear; year <= maxYear; year++) {
      const startQ = year === minYear ? minQuarter : 1;
      const endQ = year === maxYear ? maxQuarter : 4;

      for (let q = startQ; q <= endQ; q++) {
        const months: MonthData[] = [];
        const quarterStartMonth = (q - 1) * 3 + 1; // Q1: 1, Q2: 4, Q3: 7, Q4: 10

        for (let m = quarterStartMonth; m < quarterStartMonth + 3; m++) {
          const monthId = this.getMonthId(year, m);
          const monthPosts = monthMap.get(monthId) || [];

          // Sort posts: featured posts first (closest to timeline), then by date
          this.sortPostsByFeaturedAndDate(monthPosts);

          months.push({
            id: monthId,
            label: this.getLocalizedMonthLabel(m),
            year,
            month: m,
            posts: monthPosts,
          });
        }

        quarterGroups.push({
          id: `Q${q}-${year}`,
          label: this.getQuarterLabel(q, year),
          year,
          quarter: q,
          months,
        });
      }
    }

    return quarterGroups;
  }

  /**
   * Returns the current month ID (e.g., '2025-12')
   */
  public getCurrentMonthId(): string {
    const now = new Date();
    return this.getMonthId(getYear(now), getMonth(now) + 1);
  }

  /**
   * Returns month ID in YYYY-MM format
   */
  public getMonthId(year: number, month: number): string {
    return `${year}-${String(month).padStart(2, '0')}`;
  }

  /**
   * Returns month label (full or condensed)
   */
  public getMonthLabel(month: number, condensed = false): string {
    const index = month - 1;
    if (index < 0 || index > 11) return '';
    const monthKey = MONTH_KEYS[index];
    const translationKey = condensed
      ? `timeline.months_short.${monthKey}`
      : `timeline.months.${monthKey}`;
    return this.i18nService.translate(translationKey);
  }

  /**
   * Extracts year and month from a date
   */
  public getMonthFromDate(date: Date): { year: number; month: number } {
    return {
      year: getYear(date),
      month: getMonth(date) + 1, // 1-12
    };
  }

  /**
   * Filters out posts that only have Changelog category
   */
  private filterNonChangelogPosts(posts: ProductUpdate[]): ProductUpdate[] {
    return posts.filter(post => {
      const hasChangelog = post.updateCategories.includes('Changelog');
      if (hasChangelog && post.updateCategories.length === 1) {
        return false;
      }
      return !hasChangelog;
    });
  }

  /**
   * Sorts posts: featured posts first (with media), then by date
   */
  private sortPostsByFeaturedAndDate(posts: ProductUpdate[]): void {
    posts.sort((a, b) => {
      const aTranslation =
        a.translations.find(t => t.locale === a.primaryLocale) ?? a.translations[0];
      const bTranslation =
        b.translations.find(t => t.locale === b.primaryLocale) ?? b.translations[0];
      const aEnglish = a.translations.find(t => t.locale === 'en');
      const bEnglish = b.translations.find(t => t.locale === 'en');

      // Check media with English fallback
      const aFeatured = !!(
        aTranslation.featuredImage ||
        aTranslation.youtubeUrl ||
        aEnglish?.featuredImage ||
        aEnglish?.youtubeUrl
      );
      const bFeatured = !!(
        bTranslation.featuredImage ||
        bTranslation.youtubeUrl ||
        bEnglish?.featuredImage ||
        bEnglish?.youtubeUrl
      );

      // Featured posts come first
      if (aFeatured !== bFeatured) {
        return aFeatured ? -1 : 1;
      }

      // Within same featured status, sort by date
      return new Date(a.publishedAt).getTime() - new Date(b.publishedAt).getTime();
    });
  }

  /**
   * Returns the localized quarter prefix (Q for most languages, T for French)
   */
  private getQuarterPrefix(): string {
    return this.i18nService.translate('timeline.quarter_prefix');
  }

  /**
   * Returns the localized quarter label (e.g., "Q1 2025" or "T1 2025" for French)
   */
  private getQuarterLabel(quarter: number, year: number): string {
    const prefix = this.getQuarterPrefix();
    return `${prefix}${quarter} ${year}`;
  }

  /**
   * Returns the localized month label (full name)
   */
  private getLocalizedMonthLabel(month: number): string {
    return this.getMonthLabel(month, false);
  }
}
